import type { ReactElement } from 'react';
import React, { useState } from 'react';

import { TemplateProductDetails } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/TemplateProductDetails';
import { TemplateProductList } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/TemplateProductList';
import { useTemplateProductBreakdown } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/useTemplateProductBreakdown';
import { useTemplateTrackingData } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/useTemplateTrackingData';
import type { MaterialGroup } from 'src/modules/designStudio/helpers/productSelection/types';
import { useLinkOpener } from 'src/shared/hooks/useLinkOpener';
import { buttonPressed } from 'src/shared/telemetry';

enum ActiveView {
  VIEW_PRODUCTS_LIST,
  VIEW_PRODUCT_DETAILS,
}

type TemplateProductsSummaryProps = {
  onCloseMenu: () => void;
  onBack: () => void;
};

export const TemplateProductsSummary = ({
  onCloseMenu,
  onBack,
}: TemplateProductsSummaryProps) => {
  const openLink = useLinkOpener();

  const { breakdown } = useTemplateProductBreakdown();

  const [viewMode, setViewMode] = useState<ActiveView>(
    ActiveView.VIEW_PRODUCTS_LIST,
  );
  const [material, setMaterial] = useState<MaterialGroup | undefined>(
    undefined,
  );

  const { extractVariationTrackingData, commonRealProductsTrackingData } =
    useTemplateTrackingData();

  const actions = {
    [ActiveView.VIEW_PRODUCTS_LIST]: {
      onProductSelected: (v: MaterialGroup) => {
        setMaterial(v);
        setViewMode(ActiveView.VIEW_PRODUCT_DETAILS);
        buttonPressed({
          ...commonRealProductsTrackingData,
          ...extractVariationTrackingData(material!.variation!),
          track_id: 'product-additional-info-button',
          button_text: '>',
        });
      },
    },
    [ActiveView.VIEW_PRODUCT_DETAILS]: {
      onBack: () => {
        setViewMode(ActiveView.VIEW_PRODUCTS_LIST);
      },
      onOrderSample: () => {
        const url = material!.variation!.manufacturerURL;
        buttonPressed({
          ...commonRealProductsTrackingData,
          ...extractVariationTrackingData(material!.variation!),
          track_id: 'order-product-sample-button',
          button_text: 'Order product sample',
        });
        if (url) {
          openLink(url);
        }
      },
    },
  };

  const viewByMode: Record<ActiveView, ReactElement> = {
    [ActiveView.VIEW_PRODUCTS_LIST]: (
      <TemplateProductList
        closeMenu={onCloseMenu}
        onBack={onBack}
        onMaterialSelected={
          actions[ActiveView.VIEW_PRODUCTS_LIST].onProductSelected
        }
        productBreakdown={breakdown}
      />
    ),
    [ActiveView.VIEW_PRODUCT_DETAILS]: (
      <TemplateProductDetails
        closeMenu={onCloseMenu}
        material={material as MaterialGroup}
        onBack={actions[ActiveView.VIEW_PRODUCT_DETAILS].onBack}
        onOrderSample={actions[ActiveView.VIEW_PRODUCT_DETAILS].onOrderSample}
      />
    ),
  };

  return viewByMode[viewMode];
};
